import type { JudgmentGrade } from '@/types';

/** Pure timing/position math for the rhythm engine, kept free of Phaser/WebAudio so
 * it can be unit tested directly and reasoned about without a running game. */

export function computeBeat(songTime: number, bpm: number): number {
  const secPerBeat = 60 / bpm;
  return songTime / secPerBeat;
}

export function computeBeatProgress(songTime: number, bpm: number): number {
  const beat = computeBeat(songTime, bpm);
  return beat - Math.floor(beat);
}

export function computeSongProgress(songTime: number, duration: number): number {
  if (duration <= 0) return 0;
  return clamp(songTime / duration, 0, 1);
}

/** 0 at spawn, 1 at the catch line. Derived purely from the time remaining until the
 * note's target time, never accumulated frame-to-frame -- this is what prevents
 * visual drift when frames are dropped. */
export function computeNoteProgress(targetTime: number, currentTime: number, travelTime: number): number {
  if (travelTime <= 0) return 1;
  const timeUntilTarget = targetTime - currentTime;
  return 1 - timeUntilTarget / travelTime;
}

export function computeNoteY(
  targetTime: number,
  currentTime: number,
  travelTime: number,
  spawnY: number,
  catchY: number
): number {
  const progress = computeNoteProgress(targetTime, currentTime, travelTime);
  return spawnY + (catchY - spawnY) * progress;
}

/**
 * Given a note has already been confirmed as a hit (it overlapped the cat's catch
 * zone at some point within the timing window -- that part is still time-gated by
 * the caller), grades it Perfect or Good based on how centered the catch was.
 *
 * Timing alone can't distinguish skill for a hold-position mechanic: a cat parked
 * anywhere within reach will, by definition, pass through the exact target instant
 * while continuously overlapping, so "closest to the beat" trivially converges to
 * ~0ms for any sustained catch regardless of player skill. Positional precision
 * doesn't have that degeneracy -- it's something the player actively controls on
 * every note by how well they center the cat on the falling food, independent of
 * when they arrived.
 */
export function computeJudgmentForPosition(
  positionErrorPx: number,
  catchToleranceHalfWidthPx: number,
  perfectFraction: number
): Exclude<JudgmentGrade, 'miss'> {
  return positionErrorPx <= catchToleranceHalfWidthPx * perfectFraction ? 'perfect' : 'good';
}

export function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

/** Exponential "ease toward target" smoothing, frame-rate independent given deltaSeconds. */
export function smoothApproach(current: number, target: number, rate: number, deltaSeconds: number): number {
  const t = 1 - Math.exp(-rate * deltaSeconds);
  return current + (target - current) * t;
}
